// Unified Camera Player
// Single component handling both Live and Playback modes per-camera
// Live: WebRTC (go2rtc WHEP) with HLS fallback
// Playback: Driver-controlled NVR playback, video display via HLS, all controls via driver API

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Video,
  Maximize2,
  Minimize2,
  Volume2,
  VolumeX,
  Camera,
  RefreshCw,
  AlertCircle,
  Loader2,
  Play,
  Pause,
  Radio,
  SkipBack,
  SkipForward,
  Gauge,
  Wifi,
  Move,
  ChevronsLeft,
  ChevronsRight,
  Film,
  Download,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import Hls from 'hls.js';
import { Room, RoomEvent, Track, type RemoteTrack } from 'livekit-client';
import {
  streamsApi,
  ptzApi,
  recordingsApi,
  devicesApi,
  type PTZCapabilities,
  type PlaybackStatus,
  type RecordingSegment,
} from '../../services/api';
import { api } from '../../lib/api';
import { PTZControls } from '../ptz';
import RecordingDatePicker from './RecordingDatePicker';
import SnapshotEditor from './SnapshotEditor';
import { audioService } from '../../services/audio.service';
import { format, startOfDay, endOfDay, addMinutes, addDays, differenceInMinutes } from 'date-fns';
import { useViewingCamerasStore } from '../../stores/viewing-cameras.store';
import { useUIStore } from '../../stores/ui.store';

import { useTranslation } from 'react-i18next';
// ============================================
// HELPERS
// ============================================

/**
 * Draw a red rectangle on a captured snapshot image. The rectangle is
 * given in tile-relative coordinates (the on-screen tile the operator
 * dragged on); this helper maps it onto the underlying image's native
 * resolution so the overlay survives any downscaling that happens
 * later (e.g. when the dialog renders the thumbnail).
 *
 * The "object-contain" CSS used by the tile's <video> means the video
 * is letterboxed — the rendered video may not fill the tile. We
 * compute the effective letterbox so the rectangle ends up over the
 * same pixels in image-space as the operator dragged on-screen.
 */
/**
 * Crop the image to the operator's rectangle and return only that region.
 * Used to feed a focused image to the vision-LLM so the AI describes the
 * area the operator marked, not the whole frame. Uses the same letterbox
 * math as drawRectOnImage so the crop matches what the operator dragged.
 *
 * Pads the crop by ~8% on each side so the model sees a bit of surrounding
 * context (a partial person at the edge of the rectangle is more useful
 * than half a torso). Returns null if the rect is degenerate.
 */
async function cropImageToRect(
  src: Blob,
  rect: { tileW: number; tileH: number; x1: number; y1: number; x2: number; y2: number },
): Promise<Blob | null> {
  const url = URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('image load failed'));
      i.src = url;
    });
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const scale = Math.min(rect.tileW / imgW, rect.tileH / imgH);
    const renderedW = imgW * scale;
    const renderedH = imgH * scale;
    const offsetX = (rect.tileW - renderedW) / 2;
    const offsetY = (rect.tileH - renderedH) / 2;
    const toImg = (tx: number, ty: number) => ({
      x: Math.max(0, Math.min(imgW, (tx - offsetX) / scale)),
      y: Math.max(0, Math.min(imgH, (ty - offsetY) / scale)),
    });
    const a = toImg(rect.x1, rect.y1);
    const b = toImg(rect.x2, rect.y2);
    let rx = Math.min(a.x, b.x);
    let ry = Math.min(a.y, b.y);
    let rw = Math.abs(b.x - a.x);
    let rh = Math.abs(b.y - a.y);
    if (rw < 8 || rh < 8) return null;
    // 8% padding for context
    const padX = rw * 0.08;
    const padY = rh * 0.08;
    rx = Math.max(0, rx - padX);
    ry = Math.max(0, ry - padY);
    rw = Math.min(imgW - rx, rw + padX * 2);
    rh = Math.min(imgH - ry, rh + padY * 2);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(rw);
    canvas.height = Math.round(rh);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, rx, ry, rw, rh, 0, 0, rw, rh);
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function drawRectOnImage(
  src: Blob,
  rect: { tileW: number; tileH: number; x1: number; y1: number; x2: number; y2: number },
): Promise<Blob> {
  const url = URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('image load failed'));
      i.src = url;
    });
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;

    // Letterbox math for object-contain: the image fits inside the
    // tile at the larger of the two scale ratios that still bound
    // it. Excess space is centered.
    const scale = Math.min(rect.tileW / imgW, rect.tileH / imgH);
    const renderedW = imgW * scale;
    const renderedH = imgH * scale;
    const offsetX = (rect.tileW - renderedW) / 2;
    const offsetY = (rect.tileH - renderedH) / 2;

    const toImg = (tx: number, ty: number) => {
      const localX = (tx - offsetX) / scale;
      const localY = (ty - offsetY) / scale;
      return {
        x: Math.max(0, Math.min(imgW, localX)),
        y: Math.max(0, Math.min(imgH, localY)),
      };
    };

    const a = toImg(rect.x1, rect.y1);
    const b = toImg(rect.x2, rect.y2);

    const canvas = document.createElement('canvas');
    canvas.width = imgW;
    canvas.height = imgH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    ctx.drawImage(img, 0, 0);
    // Red rectangle, stroke width scaled to image size for visibility.
    const strokeW = Math.max(3, Math.round(Math.min(imgW, imgH) / 250));
    ctx.lineWidth = strokeW;
    ctx.strokeStyle = '#ef4444';
    ctx.shadowColor = 'rgba(0,0,0,0.7)';
    ctx.shadowBlur = strokeW;
    const rx = Math.min(a.x, b.x);
    const ry = Math.min(a.y, b.y);
    const rw = Math.abs(b.x - a.x);
    const rh = Math.abs(b.y - a.y);
    ctx.strokeRect(rx, ry, rw, rh);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
        'image/png',
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ============================================
// TYPES
// ============================================

export type CameraMode = 'live' | 'playback';

export interface CameraFeed {
  id: string;
  deviceId?: string;
  instanceId: string;
  channel: number;
  name: string;
  status: 'online' | 'offline' | 'loading' | 'error';
  recording: boolean;
  webrtcUrl?: string;
  hlsUrl?: string;
  sessionId?: string;
  error?: string;
  /** True when the stream URL comes directly from the VMS (no go2rtc) */
  nativeStream?: boolean;
  /** Auth token for authenticated native streams (e.g. Lumana JWT) */
  token?: string;
  /** LiveKit server URL (e.g., https://stream.lumana.ai) */
  livekitUrl?: string;
  /** LiveKit JWT token for authentication */
  livekitToken?: string;
}

interface UnifiedCameraPlayerProps {
  camera: CameraFeed;
  onRetry: () => void;
  onStreamTypeChange?: (type: 'main' | 'sub') => void;
  streamType?: 'main' | 'sub';
  compact?: boolean;
  /** Initial mode override */
  initialMode?: CameraMode;
  /** Callback when mode changes */
  onModeChange?: (mode: CameraMode) => void;
  /** Override playback start time (for armed tile popups) */
  playbackStartTime?: Date;
  /** Override playback end time (for armed tile popups) */
  playbackEndTime?: Date;
  /** Called when a playback session is started, with the session ID */
  onPlaybackSessionStart?: (sessionId: string) => void;
  /** Called periodically with the current playback position (from driver poll) */
  onPlaybackTimeUpdate?: (time: Date) => void;
  /** IANA timezone of the camera's site (e.g., 'Europe/Amsterdam'). Used for time display + date picker. */
  timezone?: string;
}

// ============================================
// CONSTANTS
// ============================================

const DRIVER_PLAYBACK_SPEEDS = [0.25, 0.5, 1, 1.5, 2, 4, 8, 16];

const TIMELINE_ZOOM_LEVELS = [
  { label: '15m', minutes: 15 },
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
] as const;

type TimelineZoomMinutes = (typeof TIMELINE_ZOOM_LEVELS)[number]['minutes'];

const SEGMENT_COLORS: Record<string, string> = {
  continuous: '#2dd4bf80', // teal
  recording: '#2dd4bf80',
  motion: '#ef444499',    // red
  alarm: '#eab308cc',     // yellow
  event: '#f97316cc',     // orange
  pre_alarm: '#8b5cf6cc', // purple
  post_alarm: '#8b5cf6cc',
  manual: '#3b82f680',    // blue
};

const isValidUUID = (str: string | undefined): boolean => {
  if (!str) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
};


// ============================================
// MAIN COMPONENT
// ============================================

export function UnifiedCameraPlayer({
  camera: cameraProp,
  onRetry,
  onStreamTypeChange,
  streamType = 'sub',
  compact = false,
  initialMode = 'live',
  onModeChange,
  playbackStartTime,
  playbackEndTime,
  onPlaybackSessionStart,
  onPlaybackTimeUpdate,
  timezone,
}: UnifiedCameraPlayerProps) {
  const { t } = useTranslation('cameras');
  // ---- Mode state ----
  const [mode, setMode] = useState<CameraMode>(initialMode);
  const [switching, setSwitching] = useState(false);

  // Sync mode when parent forces a change (e.g., Video Wall sync toolbar "Live" button)
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  // ---- Common state ----
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPTZ, setShowPTZ] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [ptzCapabilities, setPtzCapabilities] = useState<PTZCapabilities | null>(null);
  const [snapshotBlob, setSnapshotBlob] = useState<Blob | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // ---- Live mode state ----
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const roomRef = useRef<Room | null>(null);  // LiveKit room reference
  const roomIdRef = useRef(0);  // Monotonically increasing ID to track which room instance is current
  const [connectionState, setConnectionState] = useState<string>('new');
  const [webrtcFailed, setWebrtcFailed] = useState(false);
  const [livekitConnected, setLivekitConnected] = useState(false);
  const livekitReconnectCountRef = useRef(0);  // Track reconnect attempts to avoid infinite loops

  // ---- HLS refs (separate for live and playback to avoid MediaSource conflicts) ----
  const liveHlsRef = useRef<Hls | null>(null);
  const playbackHlsRef = useRef<Hls | null>(null);

  // ---- Playback mode state ----
  const [playbackSessionId, setPlaybackSessionId] = useState<string | null>(null);
  const [playbackStatus, setPlaybackStatus] = useState<PlaybackStatus | null>(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track whether HLS has been initialized for the current playback session
  const [hlsInitialized, setHlsInitialized] = useState(false);
  // Store the HLS URL from startPlayback so we can use it once streamReady
  const playbackHlsUrlRef = useRef<string | null>(null);
  // Ref tracking the latest playbackSessionId — used to guard against stale poll responses
  const playbackSessionIdRef = useRef<string | null>(null);
  // Sticky flag: true once HLS has been initialized for the current playback session.
  // Must be a ref (not a local var) so it survives effect re-runs without resetting.
  const hlsStartedRef = useRef(false);
  // JWT token for authenticated playback streams (e.g. Lumana)
  const playbackTokenRef = useRef<string | null>(null);
  // Guard: when true, live-mode teardown must NOT touch the video element (playback HLS owns it)
  const playbackOwnsVideoRef = useRef(false);
  // Guard against double-fire of seek (e.g. RecordingDatePicker firing onDateTimeSelect twice)
  const seekInProgressRef = useRef(false);
  // Debug: visible HLS state for troubleshooting playback issues
  const [hlsDebugState, setHlsDebugState] = useState<string>('');

  // ---- Timeline state ----
  const [recordingSegments, setRecordingSegments] = useState<RecordingSegment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [timelineZoom, setTimelineZoom] = useState<TimelineZoomMinutes>(60);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);

  // ---- Export selection state ----
  const [exportSelecting, setExportSelecting] = useState(false);
  const [exportStart, setExportStart] = useState<Date | null>(null);
  const [exportEnd, setExportEnd] = useState<Date | null>(null);

  // Track mount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ---- Register/unregister as "currently viewing" (per-operator) ----
  const registerViewing = useViewingCamerasStore((s) => s.registerViewing);
  const unregisterViewing = useViewingCamerasStore((s) => s.unregisterViewing);
  const viewingDeviceIdRef = useRef<string | null>(null);

  useEffect(() => {
    const deviceId = cameraProp.deviceId;
    if (deviceId && cameraProp.status !== 'error') {
      viewingDeviceIdRef.current = deviceId;
      registerViewing(deviceId);
      return () => {
        unregisterViewing(deviceId);
        viewingDeviceIdRef.current = null;
      };
    }
  }, [cameraProp.deviceId, cameraProp.status, registerViewing, unregisterViewing]);

  // ---- Auto-start stream when camera has deviceId but no URLs ----
  // This handles cases where a caller (e.g., alarm popup) provides a camera
  // object with a deviceId but no streaming URLs pre-resolved.
  const [autoStartUrls, setAutoStartUrls] = useState<Partial<CameraFeed> | null>(null);
  const autoStartAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (mode !== 'live') return;
    // Only auto-start if camera has a deviceId but no streaming URLs
    const hasUrls = cameraProp.webrtcUrl || cameraProp.hlsUrl || cameraProp.livekitUrl;
    if (hasUrls || !cameraProp.deviceId || cameraProp.status === 'loading' || cameraProp.status === 'error') return;
    // Don't retry the same device
    if (autoStartAttemptedRef.current === cameraProp.deviceId) return;
    autoStartAttemptedRef.current = cameraProp.deviceId;

    console.log('[UnifiedPlayer] Auto-starting stream for deviceId:', cameraProp.deviceId, cameraProp.name);
    streamsApi.startStreamByDevice(cameraProp.deviceId, streamType).then((result) => {
      if (!mountedRef.current) return;
      console.log('[UnifiedPlayer] Auto-start result:', {
        livekitUrl: result.livekitUrl ? 'yes' : 'no',
        webrtcUrl: result.webrtcUrl || 'none',
        hlsUrl: result.hlsUrl || 'none',
      });
      setAutoStartUrls({
        webrtcUrl: result.webrtcUrl || undefined,
        hlsUrl: result.hlsUrl || undefined,
        livekitUrl: result.livekitUrl || undefined,
        livekitToken: result.livekitToken || undefined,
        sessionId: result.sessionId,
        token: result.token || undefined,
        status: 'online',
      });
    }).catch((err) => {
      if (!mountedRef.current) return;
      console.error('[UnifiedPlayer] Auto-start failed for', cameraProp.name, err);
    });
  }, [cameraProp.deviceId, cameraProp.webrtcUrl, cameraProp.hlsUrl, cameraProp.livekitUrl, cameraProp.status, cameraProp.name, mode, streamType]);

  // Live-stream keepalive + release. One go2rtc upstream feeds all viewers of a
  // camera; the server tears it down only when the LAST viewer leaves (+ go2rtc
  // shows 0 consumers). So while we're watching we heartbeat (~2 min, paused when
  // the tab is hidden so a backgrounded tab doesn't keep the NVR pull/transcode —
  // and the cost — alive), and on unmount we release our viewer hold.
  const liveSessionId = mode === 'live' ? (autoStartUrls?.sessionId ?? cameraProp.sessionId) : undefined;
  useEffect(() => {
    if (!liveSessionId || liveSessionId.startsWith('snapshot-') || liveSessionId.startsWith('native-')) return;
    const beat = () => {
      if (document.visibilityState !== 'visible') return; // hidden tab → let it go idle
      streamsApi.keepaliveStream(liveSessionId).catch(() => {});
    };
    const interval = window.setInterval(beat, 120_000); // 2 min
    const onVisible = () => { if (document.visibilityState === 'visible') beat(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      streamsApi.releaseStream(liveSessionId).catch(() => {});
    };
  }, [liveSessionId]);

  // Merge auto-started URLs into camera prop — all downstream code uses `camera`
  const camera = useMemo<CameraFeed>(() => {
    if (!autoStartUrls) return cameraProp;
    // Only apply if prop still has no URLs
    if (cameraProp.webrtcUrl || cameraProp.hlsUrl || cameraProp.livekitUrl) return cameraProp;
    return { ...cameraProp, ...autoStartUrls };
  }, [cameraProp, autoStartUrls]);

  /**
   * Hard-reset a <video> element so the browser releases any attached MediaSource.
   * This is required before attaching a new HLS instance — without it, the old
   * MediaSource stays alive and Hls.js throws silent MEDIA_ERROR / BUFFER_APPEND_ERROR.
   */
  const resetVideoElement = useCallback((video: HTMLVideoElement) => {
    try {
      // Debug: log who called resetVideoElement when playback HLS is active
      if (playbackOwnsVideoRef.current) {
        console.warn('[UnifiedPlayer] resetVideoElement called while playback HLS owns video!', new Error().stack);
        return; // SKIP — do not reset video element while playback HLS is attached
      }
      video.pause();
      video.removeAttribute('src');
      video.load(); // forces browser to release the MediaSource
      video.srcObject = null;
    } catch (err) {
      console.warn('[UnifiedPlayer] resetVideoElement error (element may be detached):', err);
    }
  }, []);

  // ============================================
  // PTZ CAPABILITIES
  // ============================================

  useEffect(() => {
    if (isValidUUID(camera.deviceId)) {
      ptzApi.getCapabilities(camera.deviceId!).then(result => {
        if (mountedRef.current) setPtzCapabilities(result.capabilities);
      }).catch(() => {
        if (mountedRef.current) setPtzCapabilities(null);
      });
    } else {
      setPtzCapabilities(null);
    }
  }, [camera.deviceId]);

  // ============================================
  // FULLSCREEN
  // ============================================

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await container.requestFullscreen();
    }
  };

  // ============================================
  // MUTE
  // ============================================

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // ============================================
  // SNAPSHOT
  // ============================================

  /**
   * Pure snapshot-capture path. Tries the streams REST endpoint first
   * (server-side capture, full quality) and falls back to a
   * client-side canvas grab of the currently-playing <video>. Returns
   * `null` when both paths fail. No React state side effects — used
   * by both the toolbar Snapshot button (which feeds SnapshotEditor)
   * and the placement-mode click path (which opens the incident
   * dialog with the blob attached).
   */
  const captureSnapshotBlob = useCallback(async (): Promise<Blob | null> => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api/v1';
      const ch = camera.channel || 1;
      const response = await fetch(`${apiBase}/streams/snapshot/${camera.instanceId}/${ch}`);
      if (response.ok) return await response.blob();
      console.warn(`[UnifiedPlayer] Snapshot API returned ${response.status}, trying canvas`);
    } catch (err) {
      console.warn('[UnifiedPlayer] Snapshot API failed, trying canvas:', err);
    }
    const videoEl = videoRef.current;
    if (videoEl && videoEl.videoWidth > 0) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoEl, 0, 0);
          return await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.95);
          });
        }
      } catch (canvasErr) {
        console.error('[UnifiedPlayer] Canvas capture failed:', canvasErr);
      }
    }
    return null;
  }, [camera.instanceId, camera.channel]);

  const takeSnapshot = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSnapshotLoading(true);
    audioService.playSound('camera_shutter');
    try {
      const blob = await captureSnapshotBlob();
      if (blob) setSnapshotBlob(blob);
    } finally {
      setSnapshotLoading(false);
    }
  };

  // ============================================
  // INCIDENT PLACEMENT (camera tile drag while
  // useUIStore.incidentPlacementMode is active)
  // ============================================
  //
  // The operator drags a rectangle on the tile to mark the area of
  // interest. On mouseup we capture the current frame, draw the
  // rectangle onto the captured image (rectangle coords are scaled
  // from tile-relative pixels to video pixels), and open the global
  // incident dialog with the composited image.
  //
  // A bare click (mouseup at the same point as mousedown) still
  // works — the rectangle is zero-sized, no overlay drawn, just a
  // plain snapshot. Same gesture, no extra learning.

  const incidentPlacementMode = useUIStore((s) => s.incidentPlacementMode);
  const openIncidentDialogWithPrefill = useUIStore((s) => s.openIncidentDialogWithPrefill);
  const setIncidentPlacementMode = useUIStore((s) => s.setIncidentPlacementMode);

  const placementOverlayRef = useRef<HTMLDivElement | null>(null);
  // Refs (not state) for the live drag — mousemove fires ~60/s and
  // we don't want to re-render the whole player on each event. The
  // visible rectangle is updated imperatively on the DOM element
  // referenced by `placementRectElRef` below; React only re-renders
  // when the rectangle becomes visible/invisible.
  const placementDragRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const placementRectElRef = useRef<HTMLDivElement | null>(null);
  const [placementDragActive, setPlacementDragActive] = useState(false);
  // Persistent marker rectangle on the tile — survives mouseup so the
  // operator can see "this is the area I marked on this camera". Stored
  // as percentages of the tile (not absolute pixels) so it stays
  // anchored if the tile resizes. Cleared by either drawing a new rect
  // (which replaces it) or clicking the "×" dismiss button.
  const [persistedRect, setPersistedRect] = useState<
    { x1Pct: number; y1Pct: number; x2Pct: number; y2Pct: number } | null
  >(null);

  const updatePlacementRectDOM = useCallback(() => {
    const el = placementRectElRef.current;
    const drag = placementDragRef.current;
    if (!el || !drag) return;
    const x = Math.min(drag.x1, drag.x2);
    const y = Math.min(drag.y1, drag.y2);
    const w = Math.abs(drag.x2 - drag.x1);
    const h = Math.abs(drag.y2 - drag.y1);
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
  }, []);

  const onPlacementMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // left button only
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    placementDragRef.current = { x1: x, y1: y, x2: x, y2: y };
    setPlacementDragActive(true);
    // Defer DOM-write to next frame so the rect element exists.
    requestAnimationFrame(updatePlacementRectDOM);
  }, [updatePlacementRectDOM]);

  const onPlacementMouseMove = useCallback((e: React.MouseEvent) => {
    const drag = placementDragRef.current;
    if (!drag) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    drag.x2 = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    drag.y2 = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
    updatePlacementRectDOM();
  }, [updatePlacementRectDOM]);

  /**
   * Compose the captured frame with the operator's rectangle (if any)
   * and open the global incident dialog. The rectangle is drawn in
   * **video pixel space** so it survives downscaling — we map the
   * tile-relative drag rect through the actual rendered video size.
   */
  const finalizePlacementCapture = useCallback(async (
    drag: { x1: number; y1: number; x2: number; y2: number },
    tileSize: { w: number; h: number },
  ) => {
    audioService.playSound('camera_shutter');

    const deviceLookupId = camera.deviceId || camera.id;
    const [rawBlob, device] = await Promise.all([
      captureSnapshotBlob(),
      deviceLookupId
        ? devicesApi.get(deviceLookupId).catch((err) => {
            console.warn('[UnifiedPlayer] Device location lookup failed:', err);
            return null;
          })
        : Promise.resolve(null),
    ]);
    if (!rawBlob) {
      console.error('[UnifiedPlayer] Could not capture frame for incident');
      return;
    }

    // Rectangle is meaningful only when the drag covered more than a
    // few pixels — anything smaller is a click and we ship the raw
    // frame without overlay.
    const dx = Math.abs(drag.x2 - drag.x1);
    const dy = Math.abs(drag.y2 - drag.y1);
    const hasRect = dx > 4 && dy > 4 && tileSize.w > 0 && tileSize.h > 0;

    let finalBlob: Blob = rawBlob;
    // cropBlob is the rectangle-only crop used as the vision-LLM input
    // when the operator clicks "Suggest description" in the incident
    // dialog. We keep the full-frame finalBlob for the user-visible
    // snapshot (it has the rectangle drawn on it for context).
    let cropBlob: Blob | null = null;
    if (hasRect) {
      const rectArgs = {
        tileW: tileSize.w,
        tileH: tileSize.h,
        x1: Math.min(drag.x1, drag.x2),
        y1: Math.min(drag.y1, drag.y2),
        x2: Math.max(drag.x1, drag.x2),
        y2: Math.max(drag.y1, drag.y2),
      };
      try {
        finalBlob = await drawRectOnImage(rawBlob, rectArgs);
      } catch (err) {
        console.warn('[UnifiedPlayer] Rect overlay failed, using raw frame:', err);
        finalBlob = rawBlob;
      }
      try {
        cropBlob = await cropImageToRect(rawBlob, rectArgs);
      } catch (err) {
        console.warn('[UnifiedPlayer] Rect crop failed:', err);
        cropBlob = null;
      }
    }

    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    const safeName = (camera.name || 'camera').replace(/[^A-Za-z0-9._-]/g, '_');
    const filename = `snapshot-${safeName}-${ts}.${finalBlob.type === 'image/png' ? 'png' : 'jpg'}`;
    const dataUrl = URL.createObjectURL(finalBlob);
    // Pass the crop as a separate object URL on the prefill so the
    // incident dialog's "Suggest description" can send only the
    // rectangle to the vision-LLM (better focus than the whole frame).
    const cropDataUrl = cropBlob ? URL.createObjectURL(cropBlob) : undefined;
    openIncidentDialogWithPrefill(
      {
        latitude: device?.latitude ?? undefined,
        longitude: device?.longitude ?? undefined,
        snapshotDataUrl: dataUrl,
        snapshotCropDataUrl: cropDataUrl,
      },
      { blob: finalBlob, filename },
    );
  }, [camera.deviceId, camera.id, camera.name, captureSnapshotBlob, openIncidentDialogWithPrefill]);

  const onPlacementMouseUp = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = placementDragRef.current;
    placementDragRef.current = null;
    setPlacementDragActive(false);
    if (!drag) return;
    // Snapshot the tile geometry NOW, while the overlay element is
    // still mounted. We end placement mode immediately so the red
    // ring and crosshair disappear in the same frame as mouseup —
    // otherwise the operator sees the overlay linger for the duration
    // of the async snapshot fetch + device lookup + canvas composite
    // (often 200–800 ms), which reads as "stuck".
    const overlayEl = placementOverlayRef.current;
    const tileSize = overlayEl
      ? { w: overlayEl.clientWidth, h: overlayEl.clientHeight }
      : { w: 0, h: 0 };

    // Save the rect as a persistent overlay so it stays visible on
    // the tile after release. Stored as % of tile so it survives
    // resize/fullscreen. Only saved when the drag covered more than
    // a few pixels — a bare click is a snapshot, not a "mark this
    // area" gesture, so it shouldn't paint a sticky rect.
    if (tileSize.w > 0 && tileSize.h > 0) {
      const dx = Math.abs(drag.x2 - drag.x1);
      const dy = Math.abs(drag.y2 - drag.y1);
      if (dx > 4 && dy > 4) {
        setPersistedRect({
          x1Pct: (Math.min(drag.x1, drag.x2) / tileSize.w) * 100,
          y1Pct: (Math.min(drag.y1, drag.y2) / tileSize.h) * 100,
          x2Pct: (Math.max(drag.x1, drag.x2) / tileSize.w) * 100,
          y2Pct: (Math.max(drag.y1, drag.y2) / tileSize.h) * 100,
        });
      }
    }

    setIncidentPlacementMode(false);
    await finalizePlacementCapture(drag, tileSize);
  }, [finalizePlacementCapture, setIncidentPlacementMode]);

  const onPlacementMouseLeave = useCallback(() => {
    // Drag released outside the tile — discard and don't open dialog.
    placementDragRef.current = null;
    setPlacementDragActive(false);
  }, []);

  // ============================================
  // LIVE MODE - WEBRTC
  // ============================================

  const startWebRTC = useCallback(async () => {
    if (!camera.webrtcUrl || !videoRef.current) return;

    try {
      const webrtcUrl = camera.webrtcUrl;
      let streamHost: string;
      try {
        streamHost = new URL(webrtcUrl).hostname;
      } catch {
        streamHost = window.location.hostname;
      }

      const isLocal = streamHost === 'localhost' || streamHost === '127.0.0.1' ||
        streamHost.startsWith('192.168.') || streamHost.startsWith('10.') || streamHost.startsWith('172.');

      const pc = new RTCPeerConnection({
        iceServers: isLocal ? [] : [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
        iceCandidatePoolSize: isLocal ? 0 : 2,
      });
      pcRef.current = pc;

      let streamSet = false;
      pc.ontrack = (event) => {
        if (videoRef.current && event.streams[0] && !streamSet) {
          streamSet = true;
          videoRef.current.srcObject = event.streams[0];
          videoRef.current.play().then(() => {
            if (mountedRef.current) { setIsPlaying(true); }
          }).catch((err) => {
            if (err.name !== 'AbortError') console.error('[UnifiedPlayer] Play failed:', err);
          });
        }
      };

      pc.onconnectionstatechange = () => {
        if (!mountedRef.current) return;
        setConnectionState(pc.connectionState);
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setWebrtcFailed(true);
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (!mountedRef.current) return;
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          if (videoRef.current?.paused) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
          } else {
            setIsPlaying(true);
          }
        } else if (pc.iceConnectionState === 'failed') {
          setWebrtcFailed(true);
        }
      };

      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const response = await fetch(camera.webrtcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: offer.sdp,
      });

      if (!response.ok) throw new Error(`WHEP failed: ${response.status}`);
      const answerSdp = await response.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (error) {
      console.error('[UnifiedPlayer] WebRTC error:', error);
      if (mountedRef.current) setWebrtcFailed(true);
    }
  }, [camera.webrtcUrl, camera.instanceId]);

  const stopWebRTC = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    // Only touch the video element if playback HLS hasn't taken ownership
    if (videoRef.current && !playbackOwnsVideoRef.current) {
      videoRef.current.srcObject = null;
    }
    setConnectionState('new');
    setIsPlaying(false);
  }, []);

  // ============================================
  // LIVE MODE - LIVEKIT (Lumana native WebRTC)
  // ============================================

  const startLiveKit = useCallback(async () => {
    const token = livekitTokenRef.current;
    if (!camera.livekitUrl || !token || !videoRef.current) return;

    // Assign a unique ID to this room instance so the Disconnected handler
    // can check whether it's still the "current" room or a stale one.
    const myRoomId = ++roomIdRef.current;

    // Disconnect any previous room before creating a new one
    if (roomRef.current) {
      try { roomRef.current.disconnect(); } catch (_) { /* ignore */ }
      roomRef.current = null;
    }

    try {
      console.log('[LiveKit] Connecting to', camera.livekitUrl, '(room #' + myRoomId + ')');

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        // Stale room — ignore
        if (roomIdRef.current !== myRoomId) return;
        if (track.kind === Track.Kind.Video && videoRef.current) {
          console.log('[LiveKit] Video track subscribed, attaching to video element');
          track.attach(videoRef.current);
          if (mountedRef.current) {
            setIsPlaying(true);
            setLivekitConnected(true);
          }
        }
        if (track.kind === Track.Kind.Audio && videoRef.current) {
          console.log('[LiveKit] Audio track subscribed');
          track.attach(videoRef.current);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach();
      });

      room.on(RoomEvent.Disconnected, () => {
        // If a newer room has been created, this disconnect is from a stale room — ignore
        if (roomIdRef.current !== myRoomId) {
          console.log('[LiveKit] Disconnected from stale room #' + myRoomId + ' (current is #' + roomIdRef.current + ') — ignoring');
          return;
        }

        console.log('[LiveKit] Disconnected (room #' + myRoomId + ') — will reconnect internally');
        if (mountedRef.current) {
          setLivekitConnected(false);
          setIsPlaying(false);
          livekitReconnectCountRef.current++;
          const attempt = livekitReconnectCountRef.current;
          if (attempt > 5) {
            console.warn('[LiveKit] Too many reconnect attempts, giving up');
            setWebrtcFailed(true);
            return;
          }
          const delay = Math.min(2000 * Math.pow(2, attempt - 1), 32000);
          console.log(`[LiveKit] Internal reconnect attempt ${attempt}/5 in ${delay}ms`);
          setTimeout(() => {
            // Double-check we're still the current room and component is mounted
            if (mountedRef.current && roomIdRef.current === myRoomId) {
              // Reconnect using the latest token from ref (may have been refreshed)
              const freshToken = livekitTokenRef.current;
              if (camera.livekitUrl && freshToken) {
                console.log('[LiveKit] Reconnecting with fresh token...');
                // Bump room ID to invalidate this room's handlers, then start fresh
                startLiveKit();
              }
            }
          }, delay);
        }
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.log('[LiveKit] Reconnecting...');
      });

      room.on(RoomEvent.Reconnected, () => {
        console.log('[LiveKit] Reconnected');
        livekitReconnectCountRef.current = 0;
        if (mountedRef.current) setLivekitConnected(true);
      });

      await room.prepareConnection(camera.livekitUrl, token);

      // Check if we're still the current room after async operation
      if (roomIdRef.current !== myRoomId) {
        console.log('[LiveKit] Room #' + myRoomId + ' superseded during prepareConnection — aborting');
        try { room.disconnect(); } catch (_) { /* ignore */ }
        return;
      }

      await room.connect(camera.livekitUrl, token);

      // Check again after connect
      if (roomIdRef.current !== myRoomId) {
        console.log('[LiveKit] Room #' + myRoomId + ' superseded during connect — aborting');
        try { room.disconnect(); } catch (_) { /* ignore */ }
        return;
      }

      console.log('[LiveKit] Connected successfully (room #' + myRoomId + ')');
      livekitReconnectCountRef.current = 0;
      if (mountedRef.current) setLivekitConnected(true);
    } catch (error) {
      console.error('[LiveKit] Connection error:', error);
      // Only mark as failed if this is still the current room
      if (roomIdRef.current === myRoomId && mountedRef.current) {
        setWebrtcFailed(true);
      }
    }
  }, [camera.livekitUrl]);

  const stopLiveKit = useCallback(() => {
    // Bump the room ID so any in-flight Disconnected handlers from the old room are ignored
    roomIdRef.current++;
    if (roomRef.current) {
      try { roomRef.current.disconnect(); } catch (_) { /* ignore */ }
      roomRef.current = null;
    }
    // Only touch the video element if playback HLS hasn't taken ownership
    if (videoRef.current && !playbackOwnsVideoRef.current) {
      // Detach any tracks
      const tracks = videoRef.current.srcObject;
      if (tracks && 'getTracks' in tracks) {
        (tracks as MediaStream).getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
    setLivekitConnected(false);
  }, []);

  // ============================================
  // LIVE MODE - HLS FALLBACK
  // ============================================

  const hlsRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startHLSLive = useCallback(() => {
    if (!camera.hlsUrl || !videoRef.current) return;

    if (Hls.isSupported()) {
      let retryCount = 0;
      const MAX_RETRIES = 10;

      const initHLS = () => {
        if (!mountedRef.current || !videoRef.current) return;

        // Destroy previous live HLS instance on retry
        if (liveHlsRef.current) {
          liveHlsRef.current.destroy();
          liveHlsRef.current = null;
        }
        resetVideoElement(videoRef.current);

        // Tighter HLS config for better multi-camera sync in live mode
        const liveHlsConfig: Partial<Hls['config']> = {
          lowLatencyMode: true,
          liveSyncDurationCount: 1,           // Sync closer to live edge (was 2)
          liveMaxLatencyDurationCount: 3,     // Less buffer variance (was 5)
          liveDurationInfinity: true,
          enableWorker: true,
          maxBufferLength: 2,                 // Reduced buffer for lower latency (was 6)
          maxMaxBufferLength: 4,              // Reduced max buffer (was 12)
          maxBufferHole: 0.5,
          backBufferLength: 10,               // Less back buffer needed for live (was 30)
          startPosition: -1,
          manifestLoadingMaxRetry: 0, // We handle retries ourselves
          fragLoadingMaxRetry: 10,
          fragLoadingRetryDelay: 1000,
          fragLoadingMaxRetryTimeout: 30000,
          levelLoadingMaxRetry: 6,
          levelLoadingRetryDelay: 1000,
        };
        if (camera.token) {
          liveHlsConfig.xhrSetup = (xhr: XMLHttpRequest, url: string) => {
            const separator = url.includes('?') ? '&' : '?';
            xhr.open('GET', `${url}${separator}token=${camera.token}`, true);
          };
        }
        const hls = new Hls(liveHlsConfig as any);
        liveHlsRef.current = hls;
        hls.attachMedia(videoRef.current!);
        hls.loadSource(camera.hlsUrl!);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          retryCount = 0;
          videoRef.current?.play().catch(console.error);
          if (mountedRef.current) { setIsPlaying(true); setHasError(false); }
        });

        hls.on(Hls.Events.FRAG_LOADED, () => { retryCount = 0; });

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            console.warn('[UnifiedPlayer] HLS live error:', data.type, data.details);
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              if (retryCount < MAX_RETRIES && mountedRef.current) {
                retryCount++;
                // Fast retries for first attempts (go2rtc is starting up), then slow down
                const delay = retryCount <= 5 ? 1000 : Math.min(1000 * (retryCount - 4), 5000);
                console.warn(`[UnifiedPlayer] HLS retry ${retryCount}/${MAX_RETRIES} in ${delay}ms`);
                hlsRetryTimerRef.current = setTimeout(() => {
                  if (mountedRef.current) initHLS();
                }, delay);
              } else {
                console.error('[UnifiedPlayer] HLS max retries exceeded');
                if (mountedRef.current) setHasError(true);
              }
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              hls.recoverMediaError();
            } else {
              if (mountedRef.current) setHasError(true);
            }
          }
        });
      };

      initHLS();
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = camera.hlsUrl;
      videoRef.current.addEventListener('loadedmetadata', () => {
        videoRef.current?.play().catch(console.error);
        if (mountedRef.current) setIsPlaying(true);
      });
    }
  }, [camera.hlsUrl]);

  const stopLiveHLS = useCallback(() => {
    if (hlsRetryTimerRef.current) {
      clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }
    if (liveHlsRef.current) {
      liveHlsRef.current.destroy();
      liveHlsRef.current = null;
      // Only reset video element if live HLS was active — avoids clobbering
      // a playback MediaSource that was attached after mode switch
      if (videoRef.current) {
        resetVideoElement(videoRef.current);
      }
    }
    setIsPlaying(false);
  }, [resetVideoElement]);

  const stopPlaybackHLS = useCallback(() => {
    if (hlsRetryTimerRef.current) {
      clearTimeout(hlsRetryTimerRef.current);
      hlsRetryTimerRef.current = null;
    }
    playbackOwnsVideoRef.current = false;
    if (playbackHlsRef.current) {
      playbackHlsRef.current.destroy();
      playbackHlsRef.current = null;
    }
    if (videoRef.current) {
      resetVideoElement(videoRef.current);
    }
    setIsPlaying(false);
  }, [resetVideoElement]);

  // ============================================
  // LIVE MODE EFFECT
  // ============================================

  // Keep refs of volatile values so the live mode effect doesn't restart on every change.
  // The effect should only restart when the streaming URL or mode fundamentally changes,
  // NOT on token refreshes or webrtcFailed toggles (which cause unnecessary disconnect/reconnect cycles).
  const livekitTokenRef = useRef(camera.livekitToken);
  useEffect(() => { livekitTokenRef.current = camera.livekitToken; }, [camera.livekitToken]);
  const webrtcFailedRef = useRef(webrtcFailed);
  useEffect(() => { webrtcFailedRef.current = webrtcFailed; }, [webrtcFailed]);

  useEffect(() => {
    if (mode !== 'live') return;

    let cancelled = false;

    console.log('[LIVE] Live mode effect running', {
      livekitUrl: camera.livekitUrl?.substring(0, 60),
      livekitToken: camera.livekitToken ? '(present)' : '(none)',
      webrtcUrl: camera.webrtcUrl?.substring(0, 60),
      hlsUrl: camera.hlsUrl?.substring(0, 60),
      webrtcFailed: webrtcFailedRef.current,
      status: camera.status,
      hasVideoRef: !!videoRef.current,
    });

    const init = () => {
      if (cancelled || !videoRef.current) {
        if (!cancelled) requestAnimationFrame(init);
        return;
      }

      try {
        // Try LiveKit first (Lumana native WebRTC)
        if (!webrtcFailedRef.current && camera.livekitUrl && livekitTokenRef.current) {
          console.log('[LIVE] Starting LiveKit (Lumana native)');
          startLiveKit();
        }
        // Then try WHEP WebRTC (go2rtc)
        else if (!webrtcFailedRef.current && camera.webrtcUrl) {
          console.log('[LIVE] Starting WebRTC (WHEP)');
          startWebRTC();
        }
        // Fallback to HLS
        else if (camera.hlsUrl) {
          console.log('[LIVE] Starting HLS fallback');
          startHLSLive();
        } else {
          console.warn('[LIVE] No streaming URL available');
        }
      } catch (err) {
        console.error('[LIVE] Init error:', err);
        if (mountedRef.current) setHasError(true);
      }
    };

    setHasError(false);
    setIsPlaying(false);
    setLivekitConnected(false);
    init();

    return () => {
      cancelled = true;
      stopLiveKit();
      stopWebRTC();
      stopLiveHLS();
    };
    // NOTE: webrtcFailed, livekitToken, and camera.status are intentionally excluded from deps
    // and accessed via refs to prevent teardown/reconnect cycles when they change.
    // The effect only needs to re-run when streaming URLs or mode fundamentally change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, camera.livekitUrl, camera.webrtcUrl, camera.hlsUrl, startLiveKit, startWebRTC, startHLSLive, stopLiveKit, stopWebRTC, stopLiveHLS]);

  // LiveKit token refresh - tokens expire after ~10 minutes, refresh every 5 minutes
  // This does NOT tear down the connection — it just requests a fresh token via onRetry
  // so that if a reconnect is needed later, the livekitTokenRef has a valid token.
  useEffect(() => {
    if (mode !== 'live' || !livekitConnected || !camera.livekitToken) return;

    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

    const refreshInterval = setInterval(() => {
      console.log('[LiveKit] Token refresh interval — requesting fresh token (no teardown)');
      onRetry();
    }, REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [mode, livekitConnected, camera.livekitToken, onRetry]);

  // Reset webrtcFailed when a fresh LiveKit token arrives (so reconnect can use LiveKit again)
  const prevLivekitTokenRef = useRef(camera.livekitToken);
  useEffect(() => {
    if (camera.livekitToken && camera.livekitToken !== prevLivekitTokenRef.current) {
      if (webrtcFailed) {
        console.log('[LiveKit] Fresh token received — resetting webrtcFailed for reconnect');
        setWebrtcFailed(false);
      }
      prevLivekitTokenRef.current = camera.livekitToken;
    }
  }, [camera.livekitToken, webrtcFailed]);

  // ============================================
  // PLAYBACK MODE
  // ============================================

  // Initialize playback stream using go2rtc's MP4 (fMP4/MSE) endpoint.
  // go2rtc's stream.mp4 serves fragmented MP4 over HTTP that browsers play natively.
  // This is more reliable than HLS for FFmpeg-wrapped RTSP sources (no segment timing issues).
  // Falls back to HLS if no mp4Url is available (e.g. native driver streams).
  const initPlaybackStream = useCallback((mp4Url: string | undefined, hlsUrl: string) => {
    console.log('[PB] initPlaybackStream called, mp4Url:', mp4Url || 'none', 'hlsUrl:', hlsUrl?.substring(0, 80));
    if (!mountedRef.current || !videoRef.current) return;

    // Full teardown of any previous playback
    if (playbackHlsRef.current) {
      playbackHlsRef.current.destroy();
      playbackHlsRef.current = null;
    }
    resetVideoElement(videoRef.current);

    // Prefer MP4/MSE endpoint — works immediately with FFmpeg-wrapped sources
    if (mp4Url) {
      console.log('[PB] initPlaybackStream: using MP4/MSE from', mp4Url);
      const attemptMp4 = (url: string, retriesLeft: number) => {
        if (!mountedRef.current || !videoRef.current) return;
        const video = videoRef.current;
        // Track whether video data actually arrived
        let dataLoaded = false;
        let playResolved = false;
        const onLoadedData = () => {
          dataLoaded = true;
          console.log('[PB] MP4 loadeddata event fired — video has frames');
        };
        video.addEventListener('loadeddata', onLoadedData, { once: true });

        video.src = url;
        video.muted = muted;
        video.play().then(() => {
          playResolved = true;
          console.log('[PB] MP4 play() resolved, readyState:', video.readyState);
          if (mountedRef.current) { setIsPlaying(true); setHasError(false); }
        }).catch((err) => {
          playResolved = true;
          video.removeEventListener('loadeddata', onLoadedData);
          if (retriesLeft > 0) {
            console.log(`[PB] MP4 play rejected, retrying in 2s (${retriesLeft} left)...`, err?.message);
            if (videoRef.current) {
              videoRef.current.removeAttribute('src');
              videoRef.current.load();
            }
            setTimeout(() => attemptMp4(url, retriesLeft - 1), 2000);
          } else {
            console.warn('[PB] MP4 play failed after retries, falling back to HLS:', err);
            initPlaybackHLS(hlsUrl);
          }
        });

        // Safety timeout: if play() neither resolved nor rejected after 5s,
        // AND no video data arrived, force a retry.
        // go2rtc's FFmpeg needs time to connect → produce first frame.
        setTimeout(() => {
          if (!mountedRef.current || !videoRef.current) return;
          if (!playResolved && !dataLoaded && retriesLeft > 0) {
            console.log(`[PB] MP4 timeout (no data after 5s), retrying (${retriesLeft} left)...`);
            video.removeEventListener('loadeddata', onLoadedData);
            video.removeAttribute('src');
            video.load();
            setTimeout(() => attemptMp4(url, retriesLeft - 1), 500);
          } else if (playResolved && !dataLoaded && retriesLeft > 0) {
            // play() resolved but no actual video frames — browser is "playing" empty buffer
            console.log(`[PB] MP4 play resolved but no video data, retrying (${retriesLeft} left)...`);
            video.removeEventListener('loadeddata', onLoadedData);
            video.removeAttribute('src');
            video.load();
            setTimeout(() => attemptMp4(url, retriesLeft - 1), 1000);
          }
        }, 5000);
      };
      // Small delay after reset to let browser process the load() from resetVideoElement
      setTimeout(() => attemptMp4(mp4Url, 3), 200);
      setHlsInitialized(true);
      return;
    }

    // Fallback: use HLS (for native driver streams that don't go through go2rtc)
    initPlaybackHLS(hlsUrl);
  }, [resetVideoElement, muted]);

  // HLS fallback for playback (used by native driver streams or when MP4 fails)
  const initPlaybackHLS = useCallback((hlsUrl: string) => {
    if (!mountedRef.current || !videoRef.current) {
      console.warn('[PB-HLS] Skipped: mounted=', mountedRef.current, 'videoRef=', !!videoRef.current);
      return;
    }
    if (!Hls.isSupported()) {
      console.warn('[PB-HLS] HLS.js not supported, trying native video');
      // Try native HLS (Safari)
      videoRef.current.src = hlsUrl;
      videoRef.current.play().catch(console.error);
      return;
    }
    console.log('[PB-HLS] Loading HLS from', hlsUrl);
    console.log('[PB-HLS] Auth token present:', !!playbackTokenRef.current);

    if (playbackHlsRef.current) {
      playbackHlsRef.current.destroy();
      playbackHlsRef.current = null;
    }
    resetVideoElement(videoRef.current);

    let retryCount = 0;
    const MAX_RETRIES = 10;

    const doInit = () => {
      if (!mountedRef.current || !videoRef.current) return;

      if (playbackHlsRef.current) {
        playbackHlsRef.current.destroy();
        playbackHlsRef.current = null;
      }
      resetVideoElement(videoRef.current);

      const pbHlsCfg: Partial<Hls['config']> = {
        enableWorker: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferHole: 0.5,
        backBufferLength: 60,
        startPosition: 0,
        manifestLoadingMaxRetry: 3,
        manifestLoadingRetryDelay: 1000,
        fragLoadingMaxRetry: 10,
        fragLoadingRetryDelay: 1000,
        fragLoadingMaxRetryTimeout: 30000,
        debug: false,
      };
      const authToken = playbackTokenRef.current || camera.token;
      console.log('[PB-HLS] authToken type:', typeof authToken, 'value preview:', typeof authToken === 'string' ? authToken.substring(0, 40) : String(authToken));
      if (authToken && typeof authToken === 'string' && authToken.length > 10) {
        console.log('[PB-HLS] Setting up xhrSetup with token (len=' + authToken.length + ')');
        pbHlsCfg.xhrSetup = (xhr: XMLHttpRequest, url: string) => {
          // Append token as query parameter — re-open with modified URL
          const separator = url.includes('?') ? '&' : '?';
          const authedUrl = `${url}${separator}token=${encodeURIComponent(authToken)}`;
          xhr.open('GET', authedUrl, true);
        };
      }
      const hls = new Hls(pbHlsCfg as any);
      playbackHlsRef.current = hls;

      // Mark that playback HLS now owns the video element — prevents live teardown from clobbering it
      playbackOwnsVideoRef.current = true;

      hls.attachMedia(videoRef.current!);

      // Append token to the manifest URL as well so HLS.js initial fetch is authenticated
      let sourceUrl = hlsUrl;
      if (authToken && typeof authToken === 'string' && authToken.length > 10) {
        const separator = hlsUrl.includes('?') ? '&' : '?';
        sourceUrl = `${hlsUrl}${separator}token=${encodeURIComponent(authToken)}`;
      }
      setHlsDebugState('loadSource...');
      console.log('[PB-HLS] Loading source:', sourceUrl.substring(0, 120));
      hls.loadSource(sourceUrl);

      hls.on(Hls.Events.MANIFEST_LOADING, () => {
        setHlsDebugState('fetching manifest...');
      });

      hls.on(Hls.Events.MANIFEST_LOADED, (_, data) => {
        console.log('[PB-HLS] Manifest loaded, levels:', data.levels?.length, 'status:', data.networkDetails?.status);
        setHlsDebugState(`manifest loaded (${data.levels?.length} levels)`);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        console.log('[PB-HLS] Manifest parsed, levels:', data.levels?.length);
        setHlsDebugState('manifest parsed, playing...');
        retryCount = 0;
        videoRef.current?.play().catch((err) => {
          console.warn('[PB-HLS] play() rejected:', err?.message);
          setHlsDebugState(`play rejected: ${err?.message}`);
        });
        if (mountedRef.current) { setIsPlaying(true); setHasError(false); }
      });

      hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
        console.log('[PB-HLS] Level loaded, fragments:', data.details?.fragments?.length, 'live:', data.details?.live);
      });

      hls.on(Hls.Events.FRAG_LOADING, () => {
        setHlsDebugState('loading segment...');
      });

      hls.on(Hls.Events.FRAG_LOADED, () => {
        retryCount = 0;
        setHlsDebugState('segment loaded');
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.warn('[PB-HLS] Error:', data.type, data.details, 'fatal:', data.fatal, 'url:', (data as any).url?.substring(0, 100), 'response:', (data as any).response?.code);
        setHlsDebugState(`ERR: ${data.details} (fatal=${data.fatal})`);
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (retryCount < MAX_RETRIES && mountedRef.current) {
              retryCount++;
              const delay = Math.min(1000 * retryCount, 5000);
              console.warn(`[PB-HLS] Retry ${retryCount}/${MAX_RETRIES} in ${delay}ms`);
              setHlsDebugState(`retry ${retryCount}/${MAX_RETRIES}...`);
              hlsRetryTimerRef.current = setTimeout(() => {
                if (mountedRef.current) doInit();
              }, delay);
            } else {
              console.error('[PB-HLS] Max retries exceeded');
              setHlsDebugState('FAILED: max retries exceeded');
              if (mountedRef.current) setHasError(true);
            }
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            console.warn('[PB-HLS] Media error, attempting recovery');
            setHlsDebugState('media error, recovering...');
            hls.recoverMediaError();
          }
        }
      });

      hls.on(Hls.Events.DESTROYING, () => {
        console.warn('[PB-HLS] HLS instance being destroyed!');
        setHlsDebugState('DESTROYED');
      });
    };

    doInit();
    setHlsInitialized(true);
  }, []);

  const startPlayback = useCallback(async (startTime?: Date) => {
    if (!isValidUUID(camera.deviceId)) {
      console.warn('[PB] Cannot start playback: no valid deviceId');
      return;
    }
    console.log('[PB] startPlayback called, deviceId:', camera.deviceId, 'startTime:', startTime?.toISOString() || 'default (now-1h)');
    setPlaybackLoading(true);
    setHlsInitialized(false);
    hlsStartedRef.current = false;
    playbackHlsUrlRef.current = null;
    playbackTokenRef.current = null;
    try {
      const playStartTime = startTime || playbackStartTime || new Date(Date.now() - 30 * 1000); // Default: 30 seconds ago (near-live)
      const playEndTime = playbackEndTime || new Date(Date.now() + 60 * 60 * 1000); // Up to 1 hour ahead
      const session = await recordingsApi.startPlayback(
        camera.deviceId!,
        playStartTime,
        playEndTime,
        streamType,
      );
      const sessionAny = session as any;
      // Extract token — Lumana returns a JWT string, but guard against nested objects
      let tokenStr: string | null = null;
      const rawToken = session.token;
      console.log('[PB] Raw token info:', { type: typeof rawToken, isNull: rawToken === null, isUndefined: rawToken === undefined, preview: typeof rawToken === 'string' ? rawToken.substring(0, 50) : JSON.stringify(rawToken)?.substring(0, 80) });
      if (typeof rawToken === 'string' && rawToken.length > 0) {
        tokenStr = rawToken;
      } else if (rawToken && typeof rawToken === 'object') {
        // Try common JWT wrapper shapes
        const obj = rawToken as any;
        tokenStr = obj.token || obj.jwt || obj.accessToken || obj.access_token || null;
        if (!tokenStr && typeof obj === 'object') {
          // Last resort: stringify and check if it looks like a JWT
          const str = JSON.stringify(obj);
          console.warn('[PB] Token is object, stringified:', str.substring(0, 100));
        }
      }
      console.log('[PB] startPlayback response:', { sessionId: session.sessionId, hlsUrl: session.hlsUrl, mp4Url: sessionAny.mp4Url, hasToken: !!tokenStr, tokenLen: tokenStr?.length, tokenPreview: tokenStr?.substring(0, 30) });
      if (!mountedRef.current) return;

      playbackHlsUrlRef.current = session.hlsUrl;
      playbackTokenRef.current = tokenStr;
      playbackSessionIdRef.current = session.sessionId;
      setPlaybackSessionId(session.sessionId);

      // Notify parent (e.g. ArmedTilePopup) of the playback session ID for loop monitoring
      onPlaybackSessionStart?.(session.sessionId);

      // Attach playback stream — prefers MP4/MSE, falls back to HLS
      hlsStartedRef.current = true;
      initPlaybackStream(sessionAny.mp4Url, session.hlsUrl);

      // Fetch recording segments for timeline
      fetchRecordingSegments(playStartTime);
    } catch (error) {
      console.error('[UnifiedPlayer] Failed to start playback:', error);
      if (mountedRef.current) setHasError(true);
    } finally {
      if (mountedRef.current) setPlaybackLoading(false);
    }
  }, [camera.deviceId, streamType, initPlaybackStream]);

  const stopPlayback = useCallback(async () => {
    if (playbackSessionId) {
      try {
        await recordingsApi.stopPlayback(playbackSessionId);
      } catch (err) {
        console.warn('[UnifiedPlayer] Failed to stop playback:', err);
      }
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    playbackSessionIdRef.current = null;
    playbackTokenRef.current = null;
    playbackOwnsVideoRef.current = false;
    hlsStartedRef.current = false;
    setPlaybackSessionId(null);
    setPlaybackStatus(null);
    setHlsInitialized(false);
    playbackHlsUrlRef.current = null;
    stopPlaybackHLS();
  }, [playbackSessionId, stopPlaybackHLS]);

  // Auto-start playback when initialMode is 'playback' and camera has a valid deviceId
  const autoPlaybackStartedRef = useRef(false);
  useEffect(() => {
    if (initialMode !== 'playback' || mode !== 'playback') return;
    if (autoPlaybackStartedRef.current) return;
    if (!isValidUUID(camera.deviceId)) return;
    // Need the camera to be online (auto-start may have just resolved URLs)
    if (camera.status !== 'online') return;
    autoPlaybackStartedRef.current = true;
    console.log('[PB] Auto-starting playback for alarm popup, deviceId:', camera.deviceId);
    // Small delay to let video element be ready
    setTimeout(() => {
      if (mountedRef.current) startPlayback();
    }, 200);
  }, [initialMode, mode, camera.deviceId, camera.status, startPlayback]);

  // Poll playback status — lifecycle only (HLS attached immediately in startPlayback)
  useEffect(() => {
    if (mode !== 'playback' || !playbackSessionId) return;

    const poll = async () => {
      try {
        console.log('[PB] polling status for session:', playbackSessionId);
        const status = await recordingsApi.getPlaybackStatus(playbackSessionId);
        if (!mountedRef.current) return;

        const statusAny = status as any;
        console.log('[PB] status poll result:', {
          paused: status.paused,
          speed: status.speed,
          currentTime: status.currentTime,
          streamReady: statusAny.streamReady,
          hlsUrl: statusAny.hlsUrl?.substring(0, 60),
        });

        setPlaybackStatus(status);

        // Reattach stream if the backend restarted it (seek / speed / jump)
        if (statusAny.streamRestarted && statusAny.hlsUrl) {
          console.log('[PB] stream restarted → reattach stream');
          hlsStartedRef.current = true;
          playbackHlsUrlRef.current = statusAny.hlsUrl;
          initPlaybackStream(statusAny.mp4Url, statusAny.hlsUrl);
        }
      } catch (err: unknown) {
        const isExpired = err && typeof err === 'object' && 'code' in err && (err as { code: string }).code === 'SESSION_EXPIRED';
        const isGone = err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 410;
        if (isExpired || isGone) {
          // Guard: only clear state if we're still polling for the same session.
          // A new startPlayback() may have already replaced the session — don't clobber it.
          const isStale = playbackSessionIdRef.current !== playbackSessionId;
          console.warn('[PB] Session expired (poll)', {
            polledSessionId: playbackSessionId,
            currentSessionId: playbackSessionIdRef.current,
            isStale,
            errStatus: (err as any)?.status,
            errCode: (err as any)?.code,
            errMessage: (err as any)?.message,
          });
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (!isStale && mountedRef.current) {
            playbackSessionIdRef.current = null;
            hlsStartedRef.current = false;
            setPlaybackSessionId(null);
            setPlaybackStatus(null);
          }
          return;
        }
        console.error('[PB] Status poll failed:', err);
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 1000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [mode, playbackSessionId, initPlaybackStream]);

  // Fetch recording segments for timeline
  const fetchRecordingSegments = useCallback(async (date: Date) => {
    if (!isValidUUID(camera.deviceId)) return;
    try {
      const result = await recordingsApi.searchRecordings(
        camera.deviceId!,
        startOfDay(date),
        endOfDay(date),
      );
      if (mountedRef.current) setRecordingSegments(result.recordings || []);
    } catch (err) {
      console.warn('[UnifiedPlayer] Failed to fetch recordings:', err);
      if (mountedRef.current) setRecordingSegments([]);
    }
  }, [camera.deviceId]);

  // Send playback command to driver
  const sendPlaybackCommand = useCallback(async (
    action: 'play' | 'pause' | 'seek' | 'seekRelative' | 'setSpeed' | 'jumpToStart' | 'jumpToLive',
    params?: { seekTime?: string; seekOffset?: number; speed?: number },
  ) => {
    if (!playbackSessionId) {
      console.warn('[PB] sendPlaybackCommand blocked: no playbackSessionId');
      return;
    }
    console.log('[PB] sendPlaybackCommand:', action, params || '', 'sessionId:', playbackSessionId);

    // Native HLS sessions (Lumana) don't support server-side control commands.
    // Handle play/pause/speed locally on the video element; seek requires a new session.
    const isNativeSession = playbackSessionId.startsWith('native-pb-');
    if (isNativeSession) {
      const video = videoRef.current;
      if (action === 'play') {
        video?.play().catch(() => {});
        setIsPlaying(true);
        return;
      }
      if (action === 'pause') {
        video?.pause();
        setIsPlaying(false);
        return;
      }
      if (action === 'setSpeed' && params?.speed != null && video) {
        video.playbackRate = params.speed;
        return;
      }
      // seek / seekRelative / jumpToStart / jumpToLive — need a new playback session
      let targetTime: Date | undefined;
      if (action === 'seek' && params?.seekTime) {
        targetTime = new Date(params.seekTime);
      } else if (action === 'seekRelative' && params?.seekOffset != null) {
        const currentMs = playbackStatus?.currentTime
          ? new Date(playbackStatus.currentTime).getTime()
          : Date.now();
        targetTime = new Date(currentMs + params.seekOffset * 1000);
      } else if (action === 'jumpToStart') {
        targetTime = playbackStatus?.bufferStart
          ? new Date(playbackStatus.bufferStart)
          : new Date(Date.now() - 60 * 60 * 1000);
      } else if (action === 'jumpToLive') {
        targetTime = new Date(Date.now() - 30 * 1000); // 30s ago
      }
      console.log('[PB] Native HLS session — starting new playback at:', targetTime?.toISOString());
      // Stop current native session and start new one
      stopPlaybackHLS();
      if (playbackHlsRef.current) { playbackHlsRef.current.destroy(); playbackHlsRef.current = null; }
      playbackOwnsVideoRef.current = false;
      try { await recordingsApi.stopPlayback(playbackSessionId); } catch { /* ignore */ }
      playbackSessionIdRef.current = null;
      setPlaybackSessionId(null);
      startPlayback(targetTime);
      return;
    }

    // Non-native sessions: send control command to driver via backend
    try {
      const status = await recordingsApi.controlPlayback(playbackSessionId, { action, ...params });
      console.log('[PB] control response:', { paused: status.paused, speed: status.speed, streamRestarted: status.streamRestarted, newSessionId: status.sessionId, hlsUrl: status.hlsUrl?.substring(0, 60) });
      if (!mountedRef.current) return;

      // Sync video element play/pause state with server response
      const video = videoRef.current;
      if (video) {
        if (action === 'pause' || status.paused) {
          video.pause();
          setIsPlaying(false);
        } else if (action === 'play' && !status.paused) {
          video.play().catch(() => {});
          setIsPlaying(true);
        }
      }

      // Handle stream restart (stateless RTSP seek — backend restarted the go2rtc stream)
      const statusAny = status as any;
      if (status.streamRestarted && (statusAny.mp4Url || status.hlsUrl)) {
        console.log('[PB] Stream restarted after', action, '→ new session:', status.sessionId, 'mp4Url:', statusAny.mp4Url, 'hlsUrl:', status.hlsUrl);

        // Destroy current playback stream
        if (playbackHlsRef.current) {
          playbackHlsRef.current.destroy();
          playbackHlsRef.current = null;
        }
        if (videoRef.current) resetVideoElement(videoRef.current);
        setIsPlaying(false);

        // Update session ID
        playbackSessionIdRef.current = status.sessionId;
        setPlaybackSessionId(status.sessionId);
        playbackHlsUrlRef.current = status.hlsUrl || '';

        // Immediately reattach stream — don't wait for poll
        hlsStartedRef.current = true;
        initPlaybackStream(statusAny.mp4Url, status.hlsUrl || '');
      }

      setPlaybackStatus(status);
    } catch (error: unknown) {
      const isExpired = error && typeof error === 'object' && 'code' in error && (error as { code: string }).code === 'SESSION_EXPIRED';
      const isGone = error && typeof error === 'object' && 'status' in error && (error as { status: number }).status === 410;
      if (isExpired || isGone) {
        console.warn('[PB] Session expired during', action, '— clearing session');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (mountedRef.current) {
          playbackSessionIdRef.current = null;
          hlsStartedRef.current = false;
          setPlaybackSessionId(null);
          setPlaybackStatus(null);
        }
      } else {
        console.error('[PB] Playback command FAILED:', action, error);
      }
    }
  }, [playbackSessionId, playbackStatus, initPlaybackStream, resetVideoElement, stopPlaybackHLS, startPlayback]);

  // ============================================
  // MODE SWITCHING
  // ============================================

  const switchMode = useCallback(async (newMode: CameraMode) => {
    if (newMode === mode || switching) return;
    console.log('[PB] switchMode:', mode, '→', newMode);
    setSwitching(true);

    try {
      if (mode === 'live') {
        // Stop live streams
        stopLiveKit();
        stopWebRTC();
        stopLiveHLS();
      } else {
        // Stop playback
        await stopPlayback();
      }

      setHasError(false);
      setIsPlaying(false);
      setWebrtcFailed(false);
      setLivekitConnected(false);
      setShowPTZ(false);
      setShowSpeedMenu(false);

      setMode(newMode);
      onModeChange?.(newMode);

      if (newMode === 'playback') {
        // Start playback with current time
        // Small delay to allow video element to reset
        setTimeout(() => {
          if (mountedRef.current) startPlayback();
        }, 100);
      } else if (newMode === 'live') {
        // Live and playback now use separate session IDs, so the live stream
        // should still be running. Re-request it to reconnect the player.
        console.log('[PB] switchMode → live: calling onRetry to reconnect live stream');
        onRetry();
      }
    } finally {
      if (mountedRef.current) setSwitching(false);
    }
  }, [mode, switching, stopLiveKit, stopWebRTC, stopLiveHLS, stopPlayback, startPlayback, onModeChange, onRetry]);

  // Cleanup on unmount only — use refs to access latest values without triggering re-runs
  useEffect(() => {
    return () => {
      // Use refs to access latest callback values at unmount time
      if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; }
      if (liveHlsRef.current) { liveHlsRef.current.destroy(); liveHlsRef.current = null; }
      if (playbackHlsRef.current) { playbackHlsRef.current.destroy(); playbackHlsRef.current = null; }
      playbackOwnsVideoRef.current = false;
      if (playbackSessionIdRef.current) {
        recordingsApi.stopPlayback(playbackSessionIdRef.current).catch(() => {});
      }
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // TIMELINE HELPERS
  // ============================================

  const currentPlaybackTime = useMemo<Date>(() => {
    if (playbackStatus?.currentTime) {
      try { return new Date(playbackStatus.currentTime); } catch { /* fall through */ }
    }
    return new Date();
  }, [playbackStatus?.currentTime]);

  // Report playback position back to parent (for synced playback in VideoWall)
  useEffect(() => {
    if (mode === 'playback' && playbackStatus?.currentTime && onPlaybackTimeUpdate) {
      try { onPlaybackTimeUpdate(new Date(playbackStatus.currentTime)); } catch { /* ignore */ }
    }
  }, [playbackStatus?.currentTime, mode, onPlaybackTimeUpdate]);

  const visibleStart = useMemo(() => addMinutes(currentPlaybackTime, -timelineZoom / 2), [currentPlaybackTime, timelineZoom]);
  const visibleEnd = useMemo(() => addMinutes(currentPlaybackTime, timelineZoom / 2), [currentPlaybackTime, timelineZoom]);

  const timeToPosition = useCallback((time: Date): number => {
    const diffMs = time.getTime() - visibleStart.getTime();
    const diffMin = diffMs / 60000;
    return (diffMin / timelineZoom) * 100;
  }, [visibleStart, timelineZoom]);

  const positionToTime = useCallback((pos: number): Date => {
    const mins = (pos / 100) * timelineZoom;
    return addMinutes(visibleStart, mins);
  }, [visibleStart, timelineZoom]);

  const handleTimelineSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || !playbackSessionId) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pos = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const time = positionToTime(pos);

    if (exportSelecting) {
      // Handle export range selection
      if (!exportStart) {
        setExportStart(time);
      } else if (!exportEnd) {
        const end = time > exportStart ? time : exportStart;
        const start = time > exportStart ? exportStart : time;
        setExportStart(start);
        setExportEnd(end);
        setExportSelecting(false);
      }
    } else {
      console.log('[PB] timeline click seek →', time.toISOString());
      sendPlaybackCommand('seek', { seekTime: time.toISOString() });
    }
  }, [positionToTime, playbackSessionId, sendPlaybackCommand, exportSelecting, exportStart]);

  // Track the latest drag position so we only seek once on mouseup
  const dragSeekTimeRef = useRef<string | null>(null);

  const handleTimelineMouseDown = useCallback((e: React.MouseEvent) => {
    if (exportSelecting) return;
    e.preventDefault();
    setIsDraggingTimeline(true);
    // Compute initial drag position
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pos = Math.max(0, Math.min(100, (x / rect.width) * 100));
      dragSeekTimeRef.current = positionToTime(pos).toISOString();
    }
  }, [exportSelecting, positionToTime]);

  useEffect(() => {
    if (!isDraggingTimeline) return;
    const handleMove = (e: MouseEvent) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pos = Math.max(0, Math.min(100, (x / rect.width) * 100));
      // Just store the position — don't seek yet (avoid spamming stream restarts)
      dragSeekTimeRef.current = positionToTime(pos).toISOString();
    };
    const handleUp = () => {
      setIsDraggingTimeline(false);
      // Seek to final drag position
      if (dragSeekTimeRef.current && playbackSessionId) {
        console.log('[PB] timeline drag release → seek to', dragSeekTimeRef.current);
        sendPlaybackCommand('seek', { seekTime: dragSeekTimeRef.current });
        dragSeekTimeRef.current = null;
      }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingTimeline, positionToTime, playbackSessionId, sendPlaybackCommand]);

  // Timeline segments rendering
  const timelineSegmentsRendered = useMemo(() => {
    return recordingSegments.map((seg, i) => {
      const segStart = new Date(seg.startTime);
      const segEnd = new Date(seg.endTime);
      const startPos = timeToPosition(segStart);
      const endPos = timeToPosition(segEnd);
      if (endPos < 0 || startPos > 100) return null;
      const left = Math.max(0, startPos);
      const right = Math.min(100, endPos);
      const width = right - left;
      if (width <= 0) return null;
      const color = SEGMENT_COLORS[seg.type] || SEGMENT_COLORS.recording;
      return (
        <div
          key={i}
          className="absolute top-0 bottom-0"
          style={{ left: `${left}%`, width: `${width}%`, backgroundColor: color }}
        />
      );
    });
  }, [recordingSegments, timeToPosition]);

  // Time markers for timeline
  const timeMarkers = useMemo(() => {
    const markers: { time: Date; position: number }[] = [];
    let intervalMin = 5;
    if (timelineZoom >= 360) intervalMin = 60;
    else if (timelineZoom >= 60) intervalMin = 15;
    else if (timelineZoom >= 15) intervalMin = 5;

    const dayStart = startOfDay(visibleStart);
    const startMin = Math.floor(differenceInMinutes(visibleStart, dayStart) / intervalMin) * intervalMin;
    let t = addMinutes(dayStart, startMin);

    while (t <= visibleEnd) {
      const pos = timeToPosition(t);
      if (pos >= 0 && pos <= 100) {
        markers.push({ time: t, position: pos });
      }
      t = addMinutes(t, intervalMin);
    }
    return markers;
  }, [visibleStart, visibleEnd, timeToPosition, timelineZoom]);

  // Format helpers
  const formatTimeHMS = (isoString?: string): string => {
    if (!isoString) return '--:--:--';
    try {
      const d = new Date(isoString);
      const opts: Intl.DateTimeFormatOptions = { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' };
      if (timezone) opts.timeZone = timezone;
      const time = d.toLocaleTimeString('en-GB', opts);
      if (timezone) {
        const tzAbbr = d.toLocaleString('en-US', { timeZone: timezone, timeZoneName: 'short' }).split(' ').pop();
        return `${time} ${tzAbbr}`;
      }
      return time;
    } catch { return '--:--:--'; }
  };

  const isPaused = playbackStatus?.paused ?? true;
  const currentSpeed = playbackStatus?.speed ?? 1;
  const hasPTZ = ptzCapabilities && (ptzCapabilities.pan || ptzCapabilities.tilt || ptzCapabilities.zoom);

  // Sync paused state for UI display only — actual pause/resume is handled by the
  // driver command to the NVR, which stops/resumes the RTSP stream natively.
  // No need to pause the <video> element or stop HLS loading.

  // Speed changes are driver commands — the NVR adjusts the RTSP stream speed natively.
  // No need to set video.playbackRate on the frontend.

  // ============================================
  // STATUS SCREENS
  // ============================================

  if (camera.status === 'loading') {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 mx-auto mb-2 animate-spin" />
          <p className="text-sm text-gray-400">
            {mode === 'live' ? 'Connecting...' : 'Loading playback...'}
          </p>
        </div>
      </div>
    );
  }

  if (camera.status === 'error' || hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">{camera.error || 'Connection error'}</p>
          <button
            onClick={() => { setHasError(false); setWebrtcFailed(false); onRetry(); }}
            className="flex items-center gap-1 px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm text-white mx-auto"
          >
            <RefreshCw className="w-4 h-4" />
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  if (camera.status === 'offline' || (!camera.webrtcUrl && !camera.hlsUrl && !camera.livekitUrl && mode === 'live')) {
    // i18n key 'cameraOffline' is often untranslated → show a real label.
    const offlineLabel = camera.status === 'offline'
      ? (t('cameraOffline') === 'cameraOffline' ? 'Camera offline' : t('cameraOffline'))
      : 'No live stream';
    // NO snapshot fallback (per request): if there's no live WebRTC/HLS stream,
    // show a clear "no live stream" state with a retry — never snapshot polling.
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Video className="w-8 h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{offlineLabel}</p>
          {camera.status !== 'offline' && (
            <button
              onClick={() => { setWebrtcFailed(false); setHasError(false); onRetry(); }}
              className="mx-auto mt-2 flex items-center gap-1 rounded bg-gray-800 px-3 py-1 text-sm text-white hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4" /> {t('retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================

  return (
    <div
      ref={containerRef}
      className={`w-full h-full relative bg-black group ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => { setShowControls(false); setShowSpeedMenu(false); }}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        autoPlay
        playsInline
        muted={muted}
      />

      {/* Persistent marker rectangle — the area the operator marked
          most recently. Stays after placement mode is dismissed so
          the operator can see "this is the bit of frame I care about
          on this camera". Replaced when they draw a new rect; cleared
          by the × dismiss button in the top-right corner. Stored as
          % of tile, so it stays anchored on resize / fullscreen. */}
      {persistedRect && !placementDragActive && (() => {
        const x = Math.min(persistedRect.x1Pct, persistedRect.x2Pct);
        const y = Math.min(persistedRect.y1Pct, persistedRect.y2Pct);
        const w = Math.abs(persistedRect.x2Pct - persistedRect.x1Pct);
        const h = Math.abs(persistedRect.y2Pct - persistedRect.y1Pct);
        return (
          <div
            className="absolute z-30 border-2 border-red-500 bg-red-500/10 pointer-events-none"
            style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPersistedRect(null); }}
              className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-red-500 hover:bg-red-400 text-white text-xs flex items-center justify-center shadow-md pointer-events-auto"
              title={t('clearMarkedArea')}
              aria-label={t('clearMarkedArea')}
            >
              ×
            </button>
          </div>
        );
      })()}

      {/* Placement-mode overlay — when the Crosshair toggle in the
          header is on, the tile becomes a "drag-to-mark + create
          incident" target. Operators drag a rectangle around the area
          of interest; on release we capture the frame, draw the
          rectangle on it, and open the global incident dialog with
          the composited image. A bare click (no drag) still works and
          ships the raw frame with no overlay. The overlay sits above
          PTZ / fullscreen / drag handles so it always wins the
          pointer events. */}
      {incidentPlacementMode && (
        <div
          ref={placementOverlayRef}
          role="button"
          aria-label={`Create incident from ${camera.name}`}
          title={`Drag to mark an area on ${camera.name}, release to create incident`}
          onMouseDown={onPlacementMouseDown}
          onMouseMove={onPlacementMouseMove}
          onMouseUp={onPlacementMouseUp}
          onMouseLeave={onPlacementMouseLeave}
          className="absolute inset-0 z-40 cursor-crosshair bg-red-500/0 hover:bg-red-500/5 ring-2 ring-red-500/60 ring-inset select-none"
        >
          {placementDragActive && (
            <div
              ref={placementRectElRef}
              className="absolute border-2 border-red-500 bg-red-500/15 pointer-events-none"
              style={{ left: 0, top: 0, width: 0, height: 0 }}
            />
          )}
        </div>
      )}

      {/* Switching mode overlay */}
      {switching && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary-500 mx-auto mb-2 animate-spin" />
            <p className="text-sm text-gray-400">{t('switchingMode')}</p>
          </div>
        </div>
      )}

      {/* Loading spinner overlay */}
      {!isPlaying && !hasError && !switching && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center">
            <Loader2 className="w-6 h-6 text-white animate-spin mx-auto" />
            {mode === 'playback' && hlsDebugState && (
              <p className="text-xs text-yellow-400 mt-2 font-mono">{hlsDebugState}</p>
            )}
          </div>
        </div>
      )}

      {/* ====== TOP BAR OSD ====== */}
      <div
        className={`absolute top-0 left-0 right-0 p-1.5 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent transition-opacity ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left: Mode badge (clickable) + REC indicator */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); switchMode(mode === 'live' ? 'playback' : 'live'); }}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold transition-colors cursor-pointer ${
              mode === 'live'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            title={mode === 'live' ? 'Switch to Playback' : 'Switch to Live'}
          >
            {mode === 'live' ? (
              <>
                <Radio className="w-3 h-3" />
                {t('live')}
              </>
            ) : (
              <>
                <Film className="w-3 h-3" />
                {t('playback')}
              </>
            )}
          </button>

          {camera.recording && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-600/80 rounded text-[10px] text-white">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              {t('rec')}
            </div>
          )}

          {/* Connection indicator in live mode */}
          {mode === 'live' && (
            livekitConnected ? (
              <div className="px-1.5 py-0.5 rounded text-[10px] bg-purple-600/50 text-purple-300">
                <Wifi className="w-2.5 h-2.5 inline me-0.5" />
                {t('livekit')}
              </div>
            ) : !webrtcFailed && camera.webrtcUrl ? (
              <div className={`px-1.5 py-0.5 rounded text-[10px] ${
                connectionState === 'connected' ? 'bg-green-600/50 text-green-300' :
                connectionState === 'connecting' ? 'bg-yellow-600/50 text-yellow-300' :
                'bg-gray-600/50 text-gray-300'
              }`}>
                <Wifi className="w-2.5 h-2.5 inline me-0.5" />
                {connectionState === 'connected' ? 'WebRTC' : connectionState}
              </div>
            ) : webrtcFailed && camera.hlsUrl ? (
              <div className="px-1.5 py-0.5 rounded text-[10px] bg-blue-600/50 text-blue-300">
                <Wifi className="w-2.5 h-2.5 inline me-0.5" />
                HLS
              </div>
            ) : null
          )}
        </div>

        {/* Right: HD/SD, Mute, Fullscreen */}
        <div className="flex items-center gap-1">
          {onStreamTypeChange && !compact && (
            <button
              onClick={(e) => { e.stopPropagation(); onStreamTypeChange(streamType === 'main' ? 'sub' : 'main'); }}
              className="px-1.5 py-0.5 bg-black/50 rounded hover:bg-black/70 text-[10px] text-white"
              title={streamType === 'main' ? 'Switch to Sub Stream' : 'Switch to Main Stream'}
            >
              {streamType === 'main' ? 'HD' : 'SD'}
            </button>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
            className="p-1 bg-black/50 rounded hover:bg-black/70"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="w-3.5 h-3.5 text-white" /> : <Volume2 className="w-3.5 h-3.5 text-white" />}
          </button>

          <button
            onClick={toggleFullscreen}
            className="p-1 bg-black/50 rounded hover:bg-black/70"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5 text-white" /> : <Maximize2 className="w-3.5 h-3.5 text-white" />}
          </button>
        </div>
      </div>

      {/* ====== BOTTOM BAR OSD ====== */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent transition-opacity ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---- PLAYBACK MODE: Timeline + Controls ---- */}
        {mode === 'playback' && !compact && (
          <div className="px-2 pt-1">
            {/* Mini timeline */}
            <div className="mb-1">
              {/* Zoom buttons */}
              <div className="flex items-center justify-between mb-0.5">
                <div className="flex items-center gap-0.5">
                  {TIMELINE_ZOOM_LEVELS.map((z) => (
                    <button
                      key={z.minutes}
                      onClick={(e) => { e.stopPropagation(); setTimelineZoom(z.minutes); }}
                      className={`px-1 py-0 text-[9px] rounded ${
                        timelineZoom === z.minutes ? 'bg-primary-600 text-white' : 'bg-black/50 text-gray-400 hover:text-white'
                      }`}
                    >
                      {z.label}
                    </button>
                  ))}
                </div>

                {/* Date nav */}
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const prevDay = addDays(selectedDate, -1);
                      setSelectedDate(prevDay);
                      fetchRecordingSegments(prevDay);
                    }}
                    className="p-0.5 hover:bg-white/20 rounded"
                  >
                    <ChevronLeft className="w-3 h-3 text-white" />
                  </button>
                  <span className="text-[10px] text-gray-300 font-mono">
                    {format(selectedDate, 'MMM dd')}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextDay = addDays(selectedDate, 1);
                      setSelectedDate(nextDay);
                      fetchRecordingSegments(nextDay);
                    }}
                    className="p-0.5 hover:bg-white/20 rounded"
                  >
                    <ChevronRight className="w-3 h-3 text-white" />
                  </button>
                </div>
              </div>

              {/* Timeline track */}
              <div
                ref={timelineRef}
                className={`relative h-3 bg-gray-700/60 rounded cursor-pointer overflow-hidden ${
                  exportSelecting ? 'ring-1 ring-green-500' : ''
                }`}
                onMouseDown={exportSelecting ? undefined : handleTimelineMouseDown}
                onClick={exportSelecting ? handleTimelineSeek : undefined}
              >
                {/* Recording segments */}
                {timelineSegmentsRendered}

                {/* Export selection overlay */}
                {exportStart && exportEnd && (
                  <div
                    className="absolute top-0 bottom-0 bg-green-500/30 border-x border-green-400 z-10"
                    style={{
                      left: `${Math.max(0, timeToPosition(exportStart))}%`,
                      width: `${Math.max(0, timeToPosition(exportEnd) - timeToPosition(exportStart))}%`,
                    }}
                  />
                )}

                {/* Time markers */}
                {timeMarkers.map((m, i) => (
                  <div key={i} className="absolute top-0 bottom-0 w-px bg-gray-500/30" style={{ left: `${m.position}%` }} />
                ))}

                {/* Playhead */}
                <div className="absolute top-0 bottom-0 w-0.5 bg-white z-20" style={{ left: '50%', transform: 'translateX(-50%)' }} />
                <div
                  className="absolute -top-0.5 z-20"
                  style={{
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderLeft: '3px solid transparent',
                    borderRight: '3px solid transparent',
                    borderTop: '3px solid white',
                  }}
                />
              </div>

              {/* Time labels */}
              <div className="relative h-3 text-[8px] text-gray-500">
                {timeMarkers.filter((_, i) => i % 2 === 0).map((m, i) => (
                  <span
                    key={i}
                    className="absolute transform -translate-x-1/2"
                    style={{ left: `${m.position}%` }}
                  >
                    {format(m.time, 'HH:mm')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Control buttons bar */}
        <div className="px-2 pb-1.5 pt-0.5">
          <div className="flex items-center justify-between">
            {/* Left controls */}
            <div className="flex items-center gap-0.5">
              {mode === 'playback' ? (
                <>
                  {/* Skip back 1m */}
                  {!compact && (
                    <button
                      onClick={(e) => { e.stopPropagation(); sendPlaybackCommand('seekRelative', { seekOffset: -60 }); }}
                      className="p-1 hover:bg-white/20 rounded"
                      title={t('skipBack1Minute')}
                    >
                      <SkipBack className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}

                  {/* Play/Pause (driver command — NVR pauses/resumes the RTSP stream) */}
                  <button
                    onClick={(e) => { e.stopPropagation(); sendPlaybackCommand(isPaused ? 'play' : 'pause'); }}
                    className="p-1 hover:bg-white/20 rounded"
                    title={isPaused ? 'Play' : 'Pause'}
                  >
                    {isPaused ? <Play className="w-4 h-4 text-white" fill="white" /> : <Pause className="w-4 h-4 text-white" fill="white" />}
                  </button>

                  {/* Skip forward 1m */}
                  {!compact && (
                    <button
                      onClick={(e) => { e.stopPropagation(); sendPlaybackCommand('seekRelative', { seekOffset: 60 }); }}
                      className="p-1 hover:bg-white/20 rounded"
                      title={t('skipForward1Minute')}
                    >
                      <SkipForward className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}

                  {/* Speed selector */}
                  {!compact && (
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowSpeedMenu(!showSpeedMenu); }}
                        className="p-1 hover:bg-white/20 rounded flex items-center gap-0.5"
                        title={t('playbackSpeed')}
                      >
                        <Gauge className="w-3.5 h-3.5 text-white" />
                        <span className="text-[10px] text-white">{currentSpeed}x</span>
                      </button>
                      {showSpeedMenu && (
                        <div className="absolute bottom-full right-0 mb-1 bg-black/95 rounded shadow-lg py-0.5 min-w-[50px] z-30">
                          {DRIVER_PLAYBACK_SPEEDS.map((speed) => (
                            <button
                              key={speed}
                              onClick={(e) => { e.stopPropagation(); sendPlaybackCommand('setSpeed', { speed }); setShowSpeedMenu(false); }}
                              className={`w-full px-2 py-0.5 text-[10px] text-start hover:bg-white/20 ${
                                currentSpeed === speed ? 'text-primary-400' : 'text-white'
                              }`}
                            >
                              {speed}x
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current time */}
                  {!compact && (
                    <span className="text-[10px] text-gray-300 font-mono ms-1">
                      {formatTimeHMS(playbackStatus?.currentTime)}
                    </span>
                  )}

                  {/* Jump to date/time button */}
                  {!compact && camera.deviceId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowDatePicker(true); }}
                      className="p-1 hover:bg-white/20 rounded ms-1"
                      title={t('jumpToDateTime')}
                    >
                      <Calendar className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}

                  {/* Go Live button */}
                  {!compact && (
                    <button
                      onClick={(e) => { e.stopPropagation(); switchMode('live'); }}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-600/70 hover:bg-red-600 rounded text-[10px] text-white font-medium ms-1"
                      title={t('switchToLive')}
                    >
                      <Radio className="w-2.5 h-2.5" />
                      {t('live')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Live mode: Snapshot + PTZ */}
                  {!compact && (
                    <button
                      onClick={takeSnapshot}
                      className="p-1 hover:bg-white/20 rounded"
                      title={t('takeSnapshot')}
                      disabled={snapshotLoading}
                    >
                      {snapshotLoading ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Camera className="w-3.5 h-3.5 text-white" />}
                    </button>
                  )}

                  {hasPTZ && !compact && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowPTZ(!showPTZ); }}
                      className={`p-1 hover:bg-white/20 rounded ${showPTZ ? 'bg-white/30' : ''}`}
                      title={t('ptzControl')}
                    >
                      <Move className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Right: Camera name + export */}
            <div className="flex items-center gap-1">
              {mode === 'playback' && !compact && (
                <>
                  {/* Export button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (exportSelecting) {
                        setExportSelecting(false);
                        setExportStart(null);
                        setExportEnd(null);
                      } else {
                        setExportSelecting(true);
                        setExportStart(null);
                        setExportEnd(null);
                      }
                    }}
                    className={`p-1 hover:bg-white/20 rounded ${exportSelecting ? 'bg-green-600/50' : ''}`}
                    title={exportSelecting ? 'Cancel export selection' : 'Select range to export'}
                  >
                    <Download className="w-3.5 h-3.5 text-white" />
                  </button>

                  {/* Export confirm */}
                  {exportStart && exportEnd && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!isValidUUID(camera.deviceId)) return;
                        try {
                          const result = await recordingsApi.getDownloadUrl(camera.deviceId!, exportStart, exportEnd);
                          if (result.downloadUrl) {
                            window.open(result.downloadUrl, '_blank');
                          }
                        } catch (err) {
                          console.error('[UnifiedPlayer] Export failed:', err);
                        }
                        setExportStart(null);
                        setExportEnd(null);
                      }}
                      className="px-1.5 py-0.5 bg-green-600 hover:bg-green-700 rounded text-[10px] text-white font-medium"
                    >
                      {t('export')} {format(exportStart, 'HH:mm')}-{format(exportEnd, 'HH:mm')}
                    </button>
                  )}

                  {/* Snapshot in playback */}
                  <button
                    onClick={takeSnapshot}
                    className="p-1 hover:bg-white/20 rounded"
                    title={t('takeSnapshot')}
                    disabled={snapshotLoading}
                  >
                    {snapshotLoading ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Camera className="w-3.5 h-3.5 text-white" />}
                  </button>
                </>
              )}

              <span className="text-[10px] text-gray-400 truncate flex-1 min-w-0" title={camera.name}>
                {camera.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ====== PTZ OVERLAY ====== */}
      {showPTZ && isValidUUID(camera.deviceId) && (
        <div
          className="absolute bottom-14 right-2 z-20"
          onClick={(e) => e.stopPropagation()}
        >
          <PTZControls deviceId={camera.deviceId!} compact onClose={() => setShowPTZ(false)} />
        </div>
      )}

      {/* Recording Date Picker Modal */}
      {camera.deviceId && (
        <RecordingDatePicker
          deviceId={camera.deviceId}
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          selectedDate={currentPlaybackTime}
          selectedTime={currentPlaybackTime}
          timezone={timezone}
          onDateTimeSelect={async (dateTime) => {
            // Guard against double-fire
            if (seekInProgressRef.current) {
              console.warn('[PB-DBG] GoToDateTime: BLOCKED — seek already in progress');
              return;
            }
            seekInProgressRef.current = true;
            console.log('[PB-DBG] === SEEK START ===');
            console.log('[PB-DBG] seekTo:', dateTime.toISOString(), 'seekMs:', dateTime.getTime());
            console.log('[PB-DBG] current sessionId:', playbackSessionIdRef.current);
            setShowDatePicker(false);
            const sessionId = playbackSessionIdRef.current;

            // Native HLS sessions (Lumana) don't support server-side seek —
            // stop the current session and start a new one at the target time
            const isNativeSession = sessionId?.startsWith('native-pb-');

            if (!sessionId || isNativeSession) {
              console.log('[PB-DBG] Starting fresh playback session', isNativeSession ? '(native HLS — no seek support)' : '(no active session)');
              seekInProgressRef.current = false;
              // Stop existing native session before starting new one
              if (isNativeSession && sessionId) {
                stopPlaybackHLS();
                if (playbackHlsRef.current) { playbackHlsRef.current.destroy(); playbackHlsRef.current = null; }
                playbackOwnsVideoRef.current = false;
                try { await recordingsApi.stopPlayback(sessionId); } catch { /* ignore */ }
                playbackSessionIdRef.current = null;
                setPlaybackSessionId(null);
              }
              startPlayback(dateTime);
              return;
            }

            // Non-native sessions: use driver control/seek
            try {
              console.log('[PB-DBG] Sending POST control/seek to backend...');
              const status = await recordingsApi.controlPlayback(sessionId, {
                action: 'seek',
                seekTime: dateTime.toISOString(),
              });
              console.log('[PB-DBG] Seek response received:', JSON.stringify({
                sessionId: status.sessionId,
                streamRestarted: (status as any).streamRestarted,
                mp4Url: (status as any).mp4Url,
                hlsUrl: status.hlsUrl,
                currentTime: status.currentTime,
              }));
              if (!mountedRef.current) return;
              const statusAny = status as any;
              if (status.streamRestarted && (statusAny.mp4Url || status.hlsUrl)) {
                console.log('[PB-DBG] Stream restarted — re-initializing stream');
                playbackSessionIdRef.current = status.sessionId;
                setPlaybackSessionId(status.sessionId);
                playbackHlsUrlRef.current = status.hlsUrl || '';
                hlsStartedRef.current = true;
                initPlaybackStream(statusAny.mp4Url, status.hlsUrl || '');
              }
              setPlaybackStatus(status);
              console.log('[PB-DBG] === SEEK COMPLETE ===');
            } catch (err) {
              console.error('[PB-DBG] === SEEK FAILED ===', err);
              // If seek failed (session expired), fall back to starting a new session
              console.log('[PB-DBG] Falling back to fresh playback session after seek failure');
              startPlayback(dateTime);
            } finally {
              seekInProgressRef.current = false;
            }
          }}
        />
      )}

      {/* Snapshot Editor Modal */}
      {snapshotBlob && (
        <SnapshotEditor
          blob={snapshotBlob}
          cameraName={camera.name}
          cameraDeviceId={camera.deviceId || camera.id}
          cameraInstanceId={camera.instanceId}
          onClose={() => setSnapshotBlob(null)}
        />
      )}
    </div>
  );
}

export default UnifiedCameraPlayer;
